import { Checkout, CheckoutStoreSelector, StoreConfig } from '@bigcommerce/checkout-sdk'
import Swal from 'sweetalert2'

import { getLanguageService } from '@bigcommerce/checkout/locale';

import { routes } from '../config'
import { tips } from '../locales'
import request from '../request'
import { beginMasqueradeCompany, endMasqueradeCompany, getAddressConfigGraphql, getCompanyCreditConfigGraphql , getInvoiceVersion } from '../request/apis'
import store from '../store'
import { setAddressConfig, setGlobalQuoteId, setInvoiceConfig } from '../store/actions'
import { constants, storage } from '../utils'

interface cartProps {
  id: string
}

export const errorFn = async (tip?: string, isBcLanguage?: boolean) => {

  const errorTip = tip || 'b3.salesRep.notMasquerading'

  await Swal.fire({
    icon: 'error',
    text: isBcLanguage ? tip : tips[errorTip],
    allowOutsideClick: false,
    allowEscapeKey: false,
  })

  if ((window as any)?.B3?.checkout_super_clear_session) sessionStorage.clear()

  const isNewStorefront = sessionStorage.getItem('isNewStorefront')

  location.href = isNewStorefront ? routes.newDashBoard : routes.dashboard

  return Promise.reject()
}


const quoteSalesRepEnter = async () => {
  const quoteCompanyId = storage.quoteCompanyId.getValue()
  const quoteCompanyName = storage.quoteCompanyName.getValue()
  const companyId = storage.B3CompanyId.getValue()

  const B3UserId = storage.B3UserId.getValue()

  if (quoteCompanyId && companyId && +quoteCompanyId !== +companyId) {
    await errorFn('b3.salesRep.notQuoteMasquerading')
  } else if (quoteCompanyId && !companyId) {
      try {
        await beginMasqueradeCompany(+quoteCompanyId, B3UserId)
        storage.B3CompanyStatus.setValue('1')
        storage.B3CompanyId.setValue(quoteCompanyId)
        storage.B3CompanyName.setValue(quoteCompanyName)
        sessionStorage.setItem('sf-isAgenting', JSON.stringify(true))
        sessionStorage.setItem('sf-companyStatus', JSON.stringify(1))
        sessionStorage.setItem('sf-salesRepCompanyId', JSON.stringify(quoteCompanyId))
        sessionStorage.setItem('sf-salesRepCompanyName', JSON.stringify(quoteCompanyName))
      } catch (error) {
        await errorFn()
      }
  } else if (!quoteCompanyId) {
    await errorFn()
  }
}

const salesRepDefinition = async () => {

  const quoteId = storage.quoteCheckoutId.getValue() || ''

  const B3CompanyStatus =!storage.B3CompanyStatus.getValue()

  if (quoteId) {
    // quote checkout enter
    await quoteSalesRepEnter()
  } else if (B3CompanyStatus) {
    await errorFn()
  }
}

const userQuoteDefinition = async () => {
  const quoteId = storage.quoteCheckoutId.getValue() || ''

  if (quoteId) {
    const quoteCompanyId = storage.quoteCompanyId.getValue()
    const companyId = storage.B3CompanyId.getValue()

    if (quoteCompanyId && companyId && +quoteCompanyId !== +companyId) {
      await errorFn('b3.salesRep.notQuoteMasquerading')
    } else if (!quoteCompanyId) {
      await errorFn()
    }
  }
}

const bcUserQuoteDefinition = async () => {
  const quoteId = storage.quoteCheckoutId.getValue() || ''

  if (quoteId) {
    const quoteCompanyId = storage.quoteCompanyId.getValue()

    if (quoteCompanyId) {
      await errorFn()
    }
  }
}


const handleClosingAgency = async () => {

  if (storage.B3RoleId.getValue() === constants.B3Role.SALESREP) {
    try {
      const salesRepCompanyId = storage.B3CompanyId.getValue()
      const B3UserId = storage.B3UserId.getValue()
  
      if (salesRepCompanyId && B3UserId) {
        await endMasqueradeCompany(B3UserId, salesRepCompanyId)
      }
    } finally {
      sessionStorage.removeItem('sf-isAgenting')
      sessionStorage.removeItem('sf-companyStatus')
      sessionStorage.removeItem('sf-salesRepCompanyId')
      sessionStorage.removeItem('sf-salesRepCompanyName')
    }
  }
}

const determineOrderType = async (data: CheckoutStoreSelector) => {

  const config: StoreConfig | undefined = data.getConfig()
  const cart = data.getCart()

  if (!config?.storeProfile?.storeHash) return

  const {
    cartType,
    referenceData,
    referenceId,
  }: any = await request.api.getCartType((cart as cartProps).id, {
    storeHash: config.storeProfile.storeHash,
  })

  if (cartType === 1) {
    storage.quoteCheckoutId.setValue(referenceId)
    storage.quoteDate.setValue(referenceData.createdAt)
    store.dispatch(setGlobalQuoteId(referenceId))

    if (cart?.id) {
      sessionStorage.setItem('cartId', cart?.id)
    }
  }

  if (cartType === 2) {
    storage.IPCheckout.setValue(1)
    store.dispatch(setGlobalQuoteId(referenceId))

    const {
      invoiceCheckoutVersion: {
        version,
      }
    } = await getInvoiceVersion()

    store.dispatch(setInvoiceConfig(+version))
    localStorage.setItem('invoicePay', '1')
  }
}

interface AddressConfigData {
  key: string,
  isEnabled: string,
}


const getAddressConfig = async (data: CheckoutStoreSelector) => {
  const config: StoreConfig | undefined = data.getConfig()
  const { addressConfig }: any = await getAddressConfigGraphql(config?.storeProfile?.storeHash || '')
  const isEnabledAddressLabel = addressConfig.find((item: AddressConfigData) => item.key === "address_label")?.isEnabled || '0'

  store.dispatch(setAddressConfig(isEnabledAddressLabel))
  storage.isEnabledAddressLabel.setValue(isEnabledAddressLabel)
}

const setCompanyCredit = async (data: CheckoutStoreSelector) => {
  const checkout: Checkout | undefined = data.getCheckout()

  if (checkout) {
    const {
      cart: {
        currency,
      },
      grandTotal,
    } = checkout

    const {
      companyCreditConfig: { limitPurchases, creditEnabled, creditCurrency, availableCredit, creditHold },
    } = await getCompanyCreditConfigGraphql()

    const isInvoice = storage.IPCheckout.getValue() === '1'

    if (!isInvoice && creditHold && creditEnabled) {
      const text = getLanguageService().translate('payment.errors.disabled_all_payment')

      await errorFn(text, true)
    }
  
    const companyCredit = {
      isEnabled: limitPurchases && creditEnabled,
      // 0: no error, 1: credit more than grandTotal,  2: mismatch
      errorType: 0,
      creditHold,
      B2BCreditCurrency: creditCurrency
    }

    if (currency.code !== creditCurrency) {
      companyCredit.errorType = 2
    } else if (+availableCredit <  +grandTotal) {
      companyCredit.errorType = 1
    }

    storage.B2BCompanyCredit.setValue(JSON.stringify(companyCredit))
  }
}

export {
  determineOrderType,
  salesRepDefinition,
  userQuoteDefinition,
  bcUserQuoteDefinition,
  handleClosingAgency,
  getAddressConfig,
  setCompanyCredit,
}